import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { IAvailableSlot, OfferDate, WorkingDay } from "../types/Response/CustomerResponse";

interface CustomCalendarProps {
    onDateTimeSelect: (date: Date, timeSlot: string) => void;
    serviceType?: "taxi" | "grooming" | "mobilegrooming" | string;
    slots?: IAvailableSlot[];
    workingDays?: WorkingDay[];
    offers?: OfferDate[];
    currentMonth?: Date;
    onMonthChange?: (date: Date) => void;
    onDateSelect?: (date: Date) => void;
}
const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const daysOfWeekHeader = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Map JS day indices (0-6) to full string names expected from API
const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
];

const CustomCalendar: React.FC<CustomCalendarProps> = ({
    onDateTimeSelect,
    serviceType,
    slots = [],
    workingDays = [],
    offers = [],
    currentMonth,
    onMonthChange,
    onDateSelect
}) => {
    const [currentDate, setCurrentDate] = useState<Date>(
        currentMonth || new Date()
    );
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);

    useEffect(() => {
        console.log("Slots received in calendar", slots);
    }, [slots]);

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Helper: Convert JS date to day string name ("Sunday", "Monday", etc.)
    const getDayNameFromDate = (date: Date): string => {
        return dayNames[date.getDay()];
    };

    // Correctly matches workingDay where dayOfWeek is a STRING
    const selectedWorkingDay = selectedDate
        ? workingDays.find((x) => {
            const dateDayName = getDayNameFromDate(selectedDate);
            return typeof x.dayOfWeek === "string"
                ? x.dayOfWeek.toLowerCase() === dateDayName.toLowerCase()
                : x.dayOfWeek === selectedDate.getDay(); // Fallback if number
        })
        : undefined;

    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const formatTime = (time: string) => {
        return time ? time.slice(0, 5) : "";
    };

    useEffect(() => {
        if (currentMonth) {
            setCurrentDate(currentMonth);
        }
    }, [currentMonth]);

    const displaySlots = slots;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const handlePrevMonth = () => {
        if (year === today.getFullYear() && month <= today.getMonth()) return;
        const newDate = new Date(year, month - 1, 1);
        setCurrentDate(newDate);
        onMonthChange?.(newDate);
    };

    const handleNextMonth = () => {
        const newDate = new Date(year, month + 1, 1);
        setCurrentDate(newDate);
        onMonthChange?.(newDate);
    };

    const handleDateClick = (day: number) => {
        const clickedDate = new Date(year, month, day);

        if (clickedDate < today) return;

        if (
            serviceType === "taxi" &&
            clickedDate.toDateString() === today.toDateString()
        ) {
            return;
        }

        setSelectedDate(clickedDate);
        setSelectedTime(null);

        onDateSelect?.(clickedDate);
    };
    const handleTimeClick = (time: string) => {
        if (!selectedDate) return;
        setSelectedTime(time);
        onDateTimeSelect(selectedDate, time);
    };

    // Helper to verify string match for working day enablement
    const isWorkingDay = (date: Date) => {
        const dateDayName = getDayNameFromDate(date);

        const day = workingDays.find((x) => {
            if (typeof x.dayOfWeek === "string") {
                return x.dayOfWeek.toLowerCase() === dateDayName.toLowerCase();
            }
            return x.dayOfWeek === date.getDay();
        });

        return day?.isEnabled ?? false;
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 p-4">
            {/* LEFT COLUMN: CALENDAR */}
            <div className="md:col-span-7 bg-white border border-[#ECE7DF] rounded-2xl p-5">
                {/* CALENDAR MONTH HEADER CONTROL */}
                <div className="flex items-center justify-between mb-6">
                    <h4 className="font-bold text-logoHeading tracking-tight">
                        {months[month]} {year}
                    </h4>
                    <div className="flex gap-1">
                        <button
                            onClick={handlePrevMonth}
                            className="p-1.5 rounded-lg bg-white border border-[#ECE7DF] hover:bg-gray-50 text-gray-600 disabled:opacity-30"
                            disabled={year === today.getFullYear() && month <= today.getMonth()}
                        >
                            <ChevronLeft size={18} />
                        </button>
                        <button
                            onClick={handleNextMonth}
                            className="p-1.5 rounded-lg bg-white border border-[#ECE7DF] hover:bg-gray-50 text-gray-600"
                        >
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>

                {/* WEEKDAYS LABELS HEADER */}
                <div className="grid grid-cols-7 text-center mb-2">
                    {daysOfWeekHeader.map((day) => (
                        <span key={day} className="text-xs font-bold text-gray-400 uppercase tracking-wider py-1">
                            {day}
                        </span>
                    ))}
                </div>

                {/* DAYS CALENDAR GRID */}
                <div className="grid grid-cols-7 gap-1 text-center">
                    {Array.from({ length: firstDayOfMonth }).map((_, idx) => (
                        <div key={`empty-${idx}`} />
                    ))}

                    {Array.from({ length: daysInMonth }).map((_, idx) => {
                        const dayNumber = idx + 1;
                        const thisDate = new Date(year, month, dayNumber);
                        const isPast = thisDate < today;

                        const isToday =
                            thisDate.toDateString() === today.toDateString();

                        const isTaxiRestricted =
                            serviceType === "taxi" && isToday;

                        const isDisabled =
                            isPast || isTaxiRestricted || !isWorkingDay(thisDate);
                        const isSelected = selectedDate?.toDateString() === thisDate.toDateString();
                        const isCurrentDay = today.toDateString() === thisDate.toDateString();

                        const offerData = offers.find(
                            (item) =>
                                new Date(item.date).toDateString() ===
                                thisDate.toDateString()
                        );

                        const offerText = !isDisabled ? offerData?.offerTitle : undefined;
                        const hasOffer = !!offerText;

                        return (
                            <button
                                key={`day-${dayNumber}`}
                                onClick={() => handleDateClick(dayNumber)}
                                disabled={isDisabled}
                                className={`
                                    h-10 w-full text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center relative
                                    ${isDisabled ? "text-gray-300 cursor-not-allowed" : "text-gray-700"}
                                    ${isSelected ? "bg-mainBrandColor text-white hover:bg-mainBrandColor shadow-md shadow-mainBrandColor/20 font-bold" : ""}
                                    ${isCurrentDay && !isSelected ? "border border-mainBrandColor text-mainBrandColor font-bold" : ""}
                                    ${hasOffer && !isSelected ? "text-orange-600 font-bold" : ""}
                                `}
                            >
                                <>
                                    {offerText && (
                                        <span
                                            className={`
                                                absolute top-0 left-1/2 -translate-x-1/2
                                                text-[9px] font-bold whitespace-nowrap animate-pulse
                                                ${isSelected ? "text-white" : "text-red-500"}
                                            `}
                                        >
                                            {offerText}
                                        </span>
                                    )}
                                    <span className={hasOffer ? "mt-3" : ""}>
                                        {dayNumber}
                                    </span>
                                </>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* RIGHT COLUMN: TIME SLOTS SELECTION PANEL */}
            <div className="md:col-span-5 flex flex-col justify-between">
                <div>
                    <div className="flex items-center gap-2 mb-4 text-logoHeading">
                        <Clock size={16} className="text-mainBrandColor" />
                        <h4 className="font-bold text-sm uppercase tracking-wider">
                            {serviceType === "taxi" ? "Available pickup Slots" : "Available Slots"}
                        </h4>
                    </div>


                    {!selectedDate ? (
                        <div className="h-[200px] border border-dashed border-[#DFD8CD] rounded-2xl flex flex-col items-center justify-center p-4 text-center">
                            <p className="text-xs text-gray-400 font-medium max-w-[180px]">
                                Please choose a date from the calendar view to load open slots
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2 pr-1">
                            {displaySlots.map((slot) => {
                                const slotTime = `${slot.startTime} - ${slot.endTime}`;
                                const isTimeSelected = selectedTime === slotTime;
                                const isFull = !slot.isAvailable || slot.capacity <= 0;
                                const remaining = slot.capacity ?? 0;
                                const isAlmostFull = false;

                                const activeOffer = offers.find(
                                    (item) =>
                                        selectedDate &&
                                        new Date(item.date).toDateString() ===
                                        selectedDate.toDateString()
                                );
                                const isGrooming = serviceType === "grooming";

                                return (
                                    <button
                                        key={`${slot.startTime}-${slot.endTime}`}
                                        onClick={() => !isFull && handleTimeClick(slotTime)}
                                        disabled={isFull}
                                        className={`
                                            relative overflow-hidden p-3 rounded-2xl border text-left transition-all duration-300 group
                                            ${isFull
                                                ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-70"
                                                : isTimeSelected
                                                    ? "bg-mainBrandColor text-white border-transparent shadow-xl scale-[1.02]"
                                                    : isAlmostFull
                                                        ? "bg-amber-50 border-amber-300 hover:border-amber-400"
                                                        : "bg-white border-[#ECE7DF] hover:border-mainBrandColor hover:shadow-md"
                                            }
                                        `}
                                    >
                                        <div className="relative z-10">
                                            <div className="flex-1">
                                                {isGrooming && activeOffer && (
                                                    <div className="mb-1">
                                                        <span className={`text-[10px] font-bold animate-pulse ${isTimeSelected ? "text-white" : "text-emerald-600"}`}>
                                                            ✨ {activeOffer?.offerTitle}
                                                        </span>
                                                    </div>
                                                )}

                                                <p
                                                    className={`text-xs font-bold text-center w-full ${isFull
                                                            ? "text-gray-400"
                                                            : isTimeSelected
                                                                ? "text-white"
                                                                : "text-logoHeading"
                                                        }`}
                                                >
                                                    {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                                                </p>

                                                {/* SLOT STATUS VISUALS */}
                                                {isGrooming && (
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <div
                                                            className={`
                                                                h-2 w-2 rounded-full
                                                                ${isFull
                                                                    ? "bg-gray-400"
                                                                    : isAlmostFull
                                                                        ? "bg-red-500 animate-pulse"
                                                                        : remaining <= 3
                                                                            ? "bg-amber-500"
                                                                            : "bg-emerald-500"}
                                                            `}
                                                        />
                                                        <p
                                                            className={`
                                                                text-[11px] font-semibold
                                                                ${isFull
                                                                    ? "text-gray-400"
                                                                    : isTimeSelected
                                                                        ? "text-white/80"
                                                                        : isAlmostFull
                                                                            ? "text-red-500"
                                                                            : "text-gray-500"}
                                                            `}
                                                        >
                                                            {isFull
                                                                ? "Fully Booked"
                                                                : isAlmostFull
                                                                    ? "Only 1 slot left"
                                                                    : `${remaining} slots`}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {selectedDate && selectedTime && (
                    <div className="mt-4 p-3.5 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-800 text-xs font-medium leading-relaxed">
                        ✨ Selected slot: <span className="font-bold">{selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span> at <span className="font-bold">{selectedTime}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomCalendar;